/**
 * Live Status Dashboard Component
 * Real-time device health monitoring with operational status buckets
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Tooltip,
  IconButton,
  useTheme,
  Chip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  FilterAlt as FilterIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { getDeviceColor } from '../utils/deviceColors';

interface DeviceStatus {
  id: string;
  entityId: string;
  entityType: 'Student' | 'RP' | 'SRP' | string;
  deviceType: 'phone' | 'watch' | 'vehicle' | string;
  lastSeen: Date;
  lastSeenMinutesAgo?: number;
  isOnline: boolean;
  signalStrength?: number;
}

export type StatusFilterType = 'nrt' | 'recent' | 'reporting' | 'idle' | 'stale' | 'offline' | null;

interface LiveStatusDashboardProps {
  devices?: DeviceStatus[];
  onRefresh?: () => void;
  isLoading?: boolean;
  timeZone?: string;
  selectedStatusFilter?: StatusFilterType;
  onStatusFilterChange?: (filter: StatusFilterType) => void;
  /**
   * When true, the entity breakdown is built purely from the entity types that
   * actually appear in `devices` and the Student/RP/SRP baseline is not seeded.
   * Use for commercial deployments where the taxonomy is data-driven.
   */
  commercial?: boolean;
  /** Optional per-type color overrides so the breakdown matches the status ticker. */
  entityTypeColors?: Record<string, string>;
}

const LiveStatusDashboard = ({
  devices = [],
  onRefresh,
  isLoading = false,
  timeZone,
  selectedStatusFilter = null,
  onStatusFilterChange,
  commercial = false,
  entityTypeColors,
}: LiveStatusDashboardProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [currentTime, setCurrentTime] = useState(new Date());

  // Get primary color from theme
  const primaryColor = theme.palette.primary.main;
  
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '76, 175, 80';
  };
  const primaryRgb = hexToRgb(primaryColor);

  // Theme-aware colors
  const colors = {
    background: isDark 
      ? 'linear-gradient(135deg, rgba(30, 32, 40, 0.98), rgba(24, 26, 32, 0.99))'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(245, 247, 250, 0.95))',
    border: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
    cardBg: isDark ? 'rgba(40, 44, 52, 0.6)' : 'rgba(255, 255, 255, 0.9)',
    text: isDark ? 'white' : '#1a1a2e',
    textSecondary: isDark ? '#9e9e9e' : '#666',
    primaryRgb,
  };

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatClockTime = (date: Date): string => date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone,
    timeZoneName: 'short',
  });

  // Reporting status buckets (like old dashboard)
  const reportingBuckets = useMemo(() => {
    const buckets = {
      nrt: { label: 'Real-Time', range: '< 1 min', devices: [] as DeviceStatus[], color: '#4caf50' },
      recent: { label: 'Recent', range: '1 - 5 min', devices: [] as DeviceStatus[], color: '#8bc34a' },
      reporting: { label: 'Reporting', range: '5 min - 4 hrs', devices: [] as DeviceStatus[], color: '#ffeb3b' },
      idle: { label: 'Idle', range: '4 - 24 hrs', devices: [] as DeviceStatus[], color: '#ff9800' },
      stale: { label: 'Stale', range: '24 - 72 hrs', devices: [] as DeviceStatus[], color: '#ff5722' },
      offline: { label: 'Offline', range: '72+ hrs', devices: [] as DeviceStatus[], color: '#f44336' },
    };

    devices.forEach(d => {
      const mins = d.lastSeenMinutesAgo ?? 999999;
      if (mins < 1) buckets.nrt.devices.push(d);
      else if (mins < 5) buckets.recent.devices.push(d);
      else if (mins < 240) buckets.reporting.devices.push(d); // 4 hours
      else if (mins < 1440) buckets.idle.devices.push(d); // 24 hours
      else if (mins < 4320) buckets.stale.devices.push(d); // 72 hours
      else buckets.offline.devices.push(d);
    });

    return buckets;
  }, [devices]);

  // Check if a device matches the selected status filter bucket
  const matchesStatusFilter = useCallback((d: DeviceStatus) => {
    if (!selectedStatusFilter) {
      return (d.lastSeenMinutesAgo ?? 999) < 30;
    }
    const mins = d.lastSeenMinutesAgo ?? 999999;
    switch (selectedStatusFilter) {
      case 'nrt': return mins < 1;
      case 'recent': return mins >= 1 && mins < 5;
      case 'reporting': return mins >= 5 && mins < 240;
      case 'idle': return mins >= 240 && mins < 1440;
      case 'stale': return mins >= 1440 && mins < 4320;
      case 'offline': return mins >= 4320;
      default: return (d.lastSeenMinutesAgo ?? 999) < 30;
    }
  }, [selectedStatusFilter]);

  type EntityBreakdownRow = {
    total: number;
    active: number;
    color: string;
    deviceTypes?: Record<string, { total: number; active: number }>;
  };

  // Entity type breakdown with device type sub-counts.
  // Gov side seeds the canonical Student/RP/SRP rows so empty categories still show
  // as "0/0". Commercial builds the breakdown purely from the entity types observed
  // in `devices`, so stale "Student 0/0" / "RP 0/0" rows never leak into the panel
  // when the data has no such types.
  const entityBreakdown = useMemo((): Record<string, EntityBreakdownRow> => {
    const fallbackPalette = ['#29b6f6', '#ab47bc', '#26a69a', '#ec407a', '#7e57c2', '#ffc107'];
    const breakdown: Record<string, EntityBreakdownRow> = commercial
      ? {}
      : {
          Student: { total: 0, active: 0, color: '#4caf50', deviceTypes: {} },
          RP: { total: 0, active: 0, color: '#ff9800' },
          SRP: { total: 0, active: 0, color: '#f44336' },
        };

    const assignColor = (type: string) => {
      if (entityTypeColors && entityTypeColors[type]) return entityTypeColors[type];
      if (type === 'Student') return '#4caf50';
      if (type === 'RP') return '#ff9800';
      if (type === 'SRP') return '#f44336';
      let hash = 0;
      for (let i = 0; i < type.length; i++) hash = (hash * 31 + type.charCodeAt(i)) >>> 0;
      return fallbackPalette[hash % fallbackPalette.length];
    };

    devices.forEach(d => {
      const type = d.entityType || 'Unknown';
      if (!breakdown[type]) {
        breakdown[type] = { total: 0, active: 0, color: assignColor(type), deviceTypes: {} };
      }
      breakdown[type].total++;
      const matches = matchesStatusFilter(d);
      if (matches) {
        breakdown[type].active++;
      }

      // Always record a device-type sub-breakdown so commercial entity types
      // (Operator, Manpack, ...) also get the Phone/Watch/Vehicle split that
      // was previously exclusive to the Student row on gov.
      if (d.deviceType) {
        const dt = breakdown[type].deviceTypes || (breakdown[type].deviceTypes = {});
        const devLabel = d.deviceType.charAt(0).toUpperCase() + d.deviceType.slice(1).toLowerCase();
        if (!dt[devLabel]) dt[devLabel] = { total: 0, active: 0 };
        dt[devLabel].total++;
        if (matches) dt[devLabel].active++;
      }
    });

    return breakdown;
  }, [devices, matchesStatusFilter, commercial, entityTypeColors]);

  // Calculate fleet health
  const fleetHealth = useMemo(() => {
    const activeCount = reportingBuckets.nrt.devices.length + reportingBuckets.recent.devices.length;
    const total = devices.length;
    const percent = total > 0 ? Math.round((activeCount / total) * 100) : 0;
    return { activeCount, total, percent };
  }, [devices, reportingBuckets]);

  // Get health color
  const getHealthColor = (percent: number) => {
    if (percent >= 80) return '#4caf50';
    if (percent >= 50) return '#ff9800';
    if (percent >= 20) return '#ff5722';
    return '#f44336';
  };

  const healthColor = getHealthColor(fleetHealth.percent);

  // Radial gauge for fleet health
  const RadialGauge = ({ percent, color, size = 100 }: { percent: number; color: string; size?: number }) => {
    const strokeWidth = 8;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percent / 100) * circumference;

    return (
      <Box sx={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{
              transition: 'stroke-dashoffset 1s ease-out',
              filter: isDark ? `drop-shadow(0 0 6px ${color})` : 'none',
            }}
          />
        </svg>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
        }}>
          <Typography sx={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: color,
            textShadow: isDark ? `0 0 20px ${color}` : 'none',
            lineHeight: 1,
          }}>
            {percent}%
          </Typography>
          <Typography sx={{ fontSize: '0.55rem', color: colors.textSecondary, mt: 0.5 }}>
            ACTIVE
          </Typography>
        </Box>
      </Box>
    );
  };

  // Status bucket row component
  const StatusBucket = ({ 
    label, 
    range, 
    count, 
    total, 
    color,
    isHighlight = false,
    isSelected = false,
    onClick,
    bucketKey
  }: { 
    label: string; 
    range: string; 
    count: number; 
    total: number;
    color: string;
    isHighlight?: boolean;
    isSelected?: boolean;
    onClick?: (key: StatusFilterType) => void;
    bucketKey: StatusFilterType;
  }) => {
    const percent = total > 0 ? (count / total) * 100 : 0;
    
    const handleClick = () => {
      if (onClick) {
        // Toggle: if already selected, clear filter
        onClick(isSelected ? null : bucketKey);
      }
    };
    
    return (
      <Box 
        onClick={handleClick}
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          py: 0.75,
          px: 1,
          borderRadius: 1,
          bgcolor: isSelected 
            ? `rgba(${hexToRgb(color)}, 0.25)` 
            : isHighlight 
              ? `rgba(${hexToRgb(color)}, 0.1)` 
              : 'transparent',
          borderLeft: `3px solid ${color}`,
          transition: 'background-color 0.2s, outline 0.2s',
          cursor: onClick ? 'pointer' : 'default',
          outline: isSelected ? `2px solid ${color}` : 'none',
          outlineOffset: -2,
          '&:hover': onClick ? {
            bgcolor: `rgba(${hexToRgb(color)}, 0.2)`,
          } : undefined
        }}
      >
        <Box sx={{ width: 80 }}>
          <Typography sx={{ 
            fontSize: '0.75rem', 
            fontWeight: isSelected ? 700 : 600, 
            color: color,
          }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', color: colors.textSecondary }}>
            {range}
          </Typography>
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <LinearProgress
            variant="determinate"
            value={percent}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
              '& .MuiLinearProgress-bar': {
                bgcolor: color,
                borderRadius: 3,
              },
            }}
          />
        </Box>
        
        <Typography sx={{ 
          fontSize: '0.9rem', 
          fontWeight: 700, 
          color: count > 0 ? color : colors.textSecondary,
          minWidth: 40,
          textAlign: 'right',
        }}>
          {count}
        </Typography>
      </Box>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        background: colors.background,
        border: `1px solid ${colors.border}`,
        borderRadius: 2,
        height: '100%',
        overflow: 'auto',
        // Minimalistic scrollbar
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          background: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
          borderRadius: 4,
        },
      }}
    >
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2,
        pb: 1.5,
        borderBottom: `1px solid ${colors.border}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ScheduleIcon sx={{ color: primaryColor, fontSize: '1.3rem' }} />
          <Box>
            <Typography sx={{ fontWeight: 700, color: colors.text, fontSize: '0.95rem' }}>
              Reporting Status
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: colors.textSecondary }}>
              {selectedStatusFilter 
                ? 'Click status to filter map' 
                : 'Click status to filter map'}
            </Typography>
          </Box>
        </Box>
        
        {/* Active Filter Indicator */}
        {selectedStatusFilter && onStatusFilterChange && (
          <Chip
            icon={<FilterIcon sx={{ fontSize: '0.9rem' }} />}
            label={reportingBuckets[selectedStatusFilter]?.label || selectedStatusFilter}
            size="small"
            onDelete={() => onStatusFilterChange(null)}
            deleteIcon={<CloseIcon sx={{ fontSize: '0.9rem' }} />}
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 600,
              bgcolor: `rgba(${hexToRgb(reportingBuckets[selectedStatusFilter]?.color || '#888')}, 0.2)`,
              color: reportingBuckets[selectedStatusFilter]?.color || '#888',
              border: `1px solid ${reportingBuckets[selectedStatusFilter]?.color || '#888'}`,
              '& .MuiChip-deleteIcon': {
                color: reportingBuckets[selectedStatusFilter]?.color || '#888',
                '&:hover': {
                  color: colors.text,
                }
              }
            }}
          />
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ 
            fontSize: '0.75rem', 
            color: colors.textSecondary,
            fontFamily: 'monospace',
          }}>
            {formatClockTime(currentTime)}
          </Typography>
          <Tooltip title="Refresh">
            <IconButton 
              size="small" 
              onClick={onRefresh}
              disabled={isLoading}
              sx={{ color: primaryColor }}
            >
              <RefreshIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main content */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        
        {/* Left: Fleet health gauge + entity breakdown */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          minWidth: 120,
        }}>
          <RadialGauge percent={fleetHealth.percent} color={healthColor} size={100} />
          
          <Typography sx={{ 
            fontSize: '0.8rem', 
            color: colors.text, 
            mt: 1,
            fontWeight: 600,
          }}>
            {fleetHealth.activeCount} / {fleetHealth.total}
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', color: colors.textSecondary }}>
            devices reporting
          </Typography>

          {/* Entity breakdown - compact, updates with selected status filter */}
          {selectedStatusFilter && (
            <Typography sx={{ 
              fontSize: '0.6rem', 
              color: reportingBuckets[selectedStatusFilter]?.color || colors.textSecondary,
              fontWeight: 600,
              mt: 1,
              mb: -0.5,
            }}>
              Showing: {reportingBuckets[selectedStatusFilter]?.label}
            </Typography>
          )}
          <Box sx={{ mt: 2, width: '100%' }}>
            {(Object.entries(entityBreakdown) as [string, EntityBreakdownRow][])
              .filter(([_, data]) => data.total > 0)
              .map(([type, data]) => (
                <Box key={type}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.3,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        bgcolor: data.color,
                      }} />
                      <Typography sx={{ fontSize: '0.65rem', color: colors.textSecondary }}>
                        {type}
                      </Typography>
                    </Box>
                    <Typography sx={{ 
                      fontSize: '0.7rem', 
                      fontWeight: 600, 
                      color: data.active > 0 ? data.color : colors.textSecondary,
                    }}>
                      {data.active}/{data.total}
                    </Typography>
                  </Box>
                  {data.deviceTypes && Object.keys(data.deviceTypes).length > 0 && (
                    <Box sx={{ pl: 2.5 }}>
                      {Object.entries(data.deviceTypes)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([devType, counts]) => {
                          // Color the device-type sub-row by the shared palette
                          // so it matches the map pins everywhere. RP/SRP groups
                          // keep their parent role color (orange/red) — every
                          // other entity bucket (Student, Operator, Manpack,
                          // Unassigned, ...commercial custom types) flows
                          // through to phone=green / watch=blue / vehicle=purple.
                          // This is what makes the legend/list/map agree even
                          // when the active preset renames "Student".
                          const isRoleGroup = type === 'RP' || type === 'SRP';
                          const deviceTypeColor = isRoleGroup
                            ? data.color
                            : getDeviceColor({ device_type: devType, entity_type: type });
                          return (
                            <Box key={devType} sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              py: 0.15,
                            }}>
                              <Typography sx={{ fontSize: '0.58rem', color: colors.textSecondary, fontStyle: 'italic' }}>
                                {devType}
                              </Typography>
                              <Typography sx={{
                                fontSize: '0.6rem',
                                color: counts.active > 0 ? deviceTypeColor : colors.textSecondary,
                                opacity: 0.8,
                              }}>
                                {counts.active}/{counts.total}
                              </Typography>
                            </Box>
                          );
                        })}
                    </Box>
                  )}
                </Box>
              ))}
          </Box>
        </Box>

        {/* Right: Reporting buckets */}
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <StatusBucket 
            bucketKey="nrt"
            label={reportingBuckets.nrt.label}
            range={reportingBuckets.nrt.range}
            count={reportingBuckets.nrt.devices.length}
            total={devices.length}
            color={reportingBuckets.nrt.color}
            isHighlight={reportingBuckets.nrt.devices.length > 0}
            isSelected={selectedStatusFilter === 'nrt'}
            onClick={onStatusFilterChange}
          />
          <StatusBucket 
            bucketKey="recent"
            label={reportingBuckets.recent.label}
            range={reportingBuckets.recent.range}
            count={reportingBuckets.recent.devices.length}
            total={devices.length}
            color={reportingBuckets.recent.color}
            isSelected={selectedStatusFilter === 'recent'}
            onClick={onStatusFilterChange}
          />
          <StatusBucket 
            bucketKey="reporting"
            label={reportingBuckets.reporting.label}
            range={reportingBuckets.reporting.range}
            count={reportingBuckets.reporting.devices.length}
            total={devices.length}
            color={reportingBuckets.reporting.color}
            isSelected={selectedStatusFilter === 'reporting'}
            onClick={onStatusFilterChange}
          />
          <StatusBucket 
            bucketKey="idle"
            label={reportingBuckets.idle.label}
            range={reportingBuckets.idle.range}
            count={reportingBuckets.idle.devices.length}
            total={devices.length}
            color={reportingBuckets.idle.color}
            isSelected={selectedStatusFilter === 'idle'}
            onClick={onStatusFilterChange}
          />
          <StatusBucket 
            bucketKey="stale"
            label={reportingBuckets.stale.label}
            range={reportingBuckets.stale.range}
            count={reportingBuckets.stale.devices.length}
            total={devices.length}
            color={reportingBuckets.stale.color}
            isSelected={selectedStatusFilter === 'stale'}
            onClick={onStatusFilterChange}
          />
          <StatusBucket 
            bucketKey="offline"
            label={reportingBuckets.offline.label}
            range={reportingBuckets.offline.range}
            count={reportingBuckets.offline.devices.length}
            total={devices.length}
            color={reportingBuckets.offline.color}
            isHighlight={reportingBuckets.offline.devices.length > 0}
            isSelected={selectedStatusFilter === 'offline'}
            onClick={onStatusFilterChange}
          />
        </Box>
      </Box>

      {/* Alert summary if many offline */}
      {reportingBuckets.offline.devices.length > devices.length * 0.5 && devices.length > 0 && (
        <Box sx={{ 
          mt: 2, 
          p: 1.5, 
          borderRadius: 1, 
          bgcolor: 'rgba(244, 67, 54, 0.1)',
          border: '1px solid rgba(244, 67, 54, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          <WarningIcon sx={{ color: '#f44336', fontSize: '1.2rem' }} />
          <Typography sx={{ fontSize: '0.75rem', color: '#f44336' }}>
            {Math.round((reportingBuckets.offline.devices.length / devices.length) * 100)}% of fleet offline ({reportingBuckets.offline.devices.length} devices)
          </Typography>
        </Box>
      )}

      {isLoading && (
        <LinearProgress 
          sx={{ 
            mt: 2, 
            borderRadius: 1,
            '& .MuiLinearProgress-bar': { bgcolor: primaryColor }
          }} 
        />
      )}
    </Paper>
  );
};

export default LiveStatusDashboard;
